import { UserPlus, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useAppNavigation, useIsSignedIn } from "@/lib/platform";
import { useIncomingInvites, useInvitePreview } from "@/lib/api";
import { clearInviteCode, getInviteCode } from "@/lib/referral";
import { getEventTypeNoun } from "@/utils/eventTypeUtils";
import { UserAPI } from "@shared/types/api";
import { useT } from "@/lib/i18n";

interface BannerEntry {
  key: string;
  code: string;
  inviterFirstName: string;
  eventType: string | null;
  neighborhoodName: string;
  eventDate: string;
}

/**
 * Dashboard banner(s) for pending "+1" invites targeted at the current user.
 *
 * Two sources:
 *   1. Server `GET /api/user/invites/incoming` — invites with this user as
 *      `inviteeUserId` or with a matching email. This is the canonical path
 *      for in-app and email invites whether or not the user has visited the
 *      preview link.
 *   2. localStorage stashed code — covers the brand-new-user case where the
 *      account didn't exist when the invite was emailed; the stashed code
 *      from the /invite/:code preview is still authoritative until the
 *      server can match by email.
 *
 * Stashed entries are deduped against server ones by code.
 */
export function PendingInviteBanner() {
  const { t } = useT();
  const isSignedIn = useIsSignedIn();
  const stashedCode = getInviteCode();
  const { navigate } = useAppNavigation();
  const { data: incomingData, isLoading: incomingLoading } =
    useIncomingInvites(isSignedIn);
  const { data: stashedPreview } = useInvitePreview(stashedCode);

  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  // Self-clean a stashed code once the server confirms it's gone.
  useEffect(() => {
    if (!stashedCode) return;
    if (!stashedPreview) return;
    if (stashedPreview.success && stashedPreview.data) {
      const status = stashedPreview.data.status;
      if (status !== "pending" && status !== "accepted") {
        clearInviteCode();
      }
    } else {
      clearInviteCode();
    }
  }, [stashedCode, stashedPreview]);

  const incomingInvites = useMemo(
    () => (incomingData?.data || []) as UserAPI.IncomingBookingInvite[],
    [incomingData?.data],
  );

  const entries: BannerEntry[] = useMemo(() => {
    const seen = new Set<string>();
    const out: BannerEntry[] = [];
    for (const inv of incomingInvites) {
      if (seen.has(inv.code)) continue;
      seen.add(inv.code);
      out.push({
        key: `srv:${inv.id}`,
        code: inv.code,
        inviterFirstName: inv.inviter.firstName,
        eventType: inv.event.type,
        neighborhoodName: inv.neighborhood.name,
        eventDate: inv.event.date,
      });
    }
    // Add the stashed-code preview only if we don't already have it from the
    // server list — avoids double-banners after the server starts matching by
    // email post-signup.
    if (stashedCode && !seen.has(stashedCode)) {
      const p = stashedPreview?.data;
      if (p && (p.status === "pending" || p.status === "accepted")) {
        out.push({
          key: `stash:${stashedCode}`,
          code: stashedCode,
          inviterFirstName: p.inviter.firstName,
          eventType: p.event.type,
          neighborhoodName: p.neighborhood.name,
          eventDate: p.event.date,
        });
      }
    }
    return out;
  }, [incomingInvites, stashedCode, stashedPreview]);

  const visible = entries.filter((e) => !dismissed.has(e.code));
  if (!isSignedIn || incomingLoading || visible.length === 0) return null;

  return (
    <div className="space-y-2 mt-4">
      {visible.map((entry) => {
        const eventNoun = getEventTypeNoun(entry.eventType);
        return (
          <div
            key={entry.key}
            className="rounded-lg border border-primary-500/40 bg-primary-500/10 px-4 py-3 flex items-center gap-3"
          >
            <UserPlus className="w-5 h-5 text-primary-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">
                {t("invites.banner.invitedTo", {
                  name: entry.inviterFirstName,
                  eventNoun,
                })}
              </div>
              <div className="text-xs text-dark-muted capitalize">
                {entry.neighborhoodName} · {entry.eventDate}
              </div>
            </div>
            <button
              onClick={() => navigate(`/invite/${entry.code}`)}
              className="btn btn-primary btn-sm"
            >
              {t("invites.banner.viewInvite")}
            </button>
            <button
              onClick={() => {
                if (entry.key.startsWith("stash:")) clearInviteCode();
                setDismissed((prev) => {
                  const next = new Set(prev);
                  next.add(entry.code);
                  return next;
                });
              }}
              className="p-1 rounded text-base-content/40 hover:text-base-content/80"
              title={t("invites.banner.dismiss")}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
